import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { MessageProvider } from '../state/MessageProvider'
import type { AnalyticsEvent, ClientConfig } from '../types'
import { ASSISTANT_NAME, ASSISTANT_ROLE, DEFAULT_ASSISTANT_AVATAR } from '../constants/assistant'
import type { BusinessContext } from '../types/widget-config'
import { ChatLauncher } from './widget/ChatLauncher'
import { ChatTranscript } from './widget/ChatTranscript'
import { Composer } from './widget/Composer'
import { SuggestionCards } from './widget/SuggestionCards'
import { BookingServiceSelection } from './widget/booking/BookingServiceSelection'
import { BookingAvailability } from './widget/booking/BookingAvailability'
import { BookingDetails } from './widget/booking/BookingDetails'
import { BookingPayment } from './widget/booking/BookingPayment'
import { BookingSubmitting } from './widget/booking/BookingSubmitting'
import { BookingSuccess } from './widget/booking/BookingSuccess'
import { mockFetchAvailability, mockFetchServices } from './widget/booking/helpers'
import { InquiryForm } from './widget/inquiry/InquiryForm'
import { InquirySubmitting } from './widget/inquiry/InquirySubmitting'
import { InquirySuccess } from './widget/inquiry/InquirySuccess'
import { WidgetHeader } from './widget/WidgetHeader'
import { BUSINESS_CONTEXT } from '../business-context'
import type {
  BookingForm,
  BookingPayment as BookingPaymentDetails,
  BookingDay,
  BookingSelection,
  BookingService,
  BookingState,
  InquiryForm as InquiryFormData,
  InquiryState,
} from './widget/types'

const normalizeDate = (value: string) => value.slice(0, 10)

type WidgetAppProps = {
  apiBaseUrl: string
  userId?: string
  calendarSettingId?: string
  chatbotId: string
  publicId: string
  businessId?: string
  clientId?: string
  brandName?: string
  assistantName?: string
  assistantRole?: string
  assistantTagline?: string
  assistantAvatar?: string
  businessContext?: BusinessContext
  timezone?: string
  primaryColor?: string
  welcomeMessage?: string
  logoUrl?: string
  phoneNumber?: string
  panelWidth?: number
  panelHeight?: number
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'
  zIndex?: number
  launcherMessage?: string
  expandedWidth?: string
  expandedHeight?: string
  mobileBreakpoint?: number
  tooltipDelay?: number
  composerPlaceholder?: string
  composerPlaceholderLoading?: string
  ctaLabels?: {
    booking: string
    inquiry: string
  }
  successMessages?: {
    bookingHeader: string
    bookingMessage: string
  }
  headers?: {
    bookAppointment: string
    leaveMessage: string
  }
  colors?: {
    backgroundColor: string
    textColor: string
    primaryColor: string
    accentColor: string
    borderColor: string
    buttonColor: string
    buttonHoverColor: string
    errorColor: string
    successColor: string
    warningColor: string
    launcherBackgroundColor: string
    headerBackgroundColor: string
    composerBackgroundColor: string
    panelBackgroundColor: string
  }
  typography?: {
    fontFamily: string
    fontSize: string
    fontWeight: string
    headingFontFamily: string
    headingFontWeight: string
  }
  initialToast?: { type: "error" | "success"; message: string } | null
  emitEvent?: (event: AnalyticsEvent) => void
}

export const WidgetApp = ({
  apiBaseUrl,
  userId = 'guest',
  calendarSettingId = '1',
  chatbotId,
  publicId,
  businessId,
  clientId,
  brandName = 'Handle Salon & Spa',
  assistantName,
  assistantRole,
  assistantTagline,
  assistantAvatar,
  businessContext,
  timezone,
  primaryColor = '#0f172a',
  welcomeMessage = 'Thanks for stopping by! Leave a note and we will reply shortly.',
  logoUrl,
  phoneNumber,
  panelWidth = 400,
  panelHeight = 460,
  position = 'bottom-right',
  zIndex = 2147483600,
  launcherMessage,
  expandedWidth = 'min(40vw, 640px)',
  expandedHeight = '70vh',
  mobileBreakpoint = 640,
  tooltipDelay = 5000,
  composerPlaceholder = 'Write a message…',
  composerPlaceholderLoading = 'Waiting for response...',
  ctaLabels = { booking: 'Book appointment', inquiry: 'Leave a message' },
  successMessages = { bookingHeader: 'All set!', bookingMessage: "Payment confirmed. We'll send reminders as your appointment approaches." },
  headers = { bookAppointment: 'Book an appointment', leaveMessage: 'Leave a message' },
  colors = {
    backgroundColor: '#FFFFFF',
    textColor: '#0f172a',
    primaryColor: '#0f172a',
    accentColor: '#3b82f6',
    borderColor: '#e2e8f0',
    buttonColor: '#0f172a',
    buttonHoverColor: '#1e293b',
    errorColor: '#ef4444',
    successColor: '#22c55e',
    warningColor: '#f59e0b',
    launcherBackgroundColor: '#0f172a',
    headerBackgroundColor: '#FFFFFF',
    composerBackgroundColor: '#FFFFFF',
    panelBackgroundColor: '#f8fafc',
  },
  typography = {
    fontFamily: "'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontSize: '14px',
    fontWeight: '400',
    headingFontFamily: "'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    headingFontWeight: '700',
  },
  initialToast = null,
  emitEvent,
}: WidgetAppProps) => {
  console.log('[WidgetApp] businessContext prop:', businessContext)
  console.log('[WidgetApp] businessContext.services:', businessContext?.services)

  const [isOpen, setIsOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [isMobileViewport, setIsMobileViewport] = useState(false)
  const [avatarFailed, setAvatarFailed] = useState(false)
  const [bookingState, setBookingState] = useState<BookingState>({ status: 'idle' })
  const [inquiryState, setInquiryState] = useState<InquiryState>({ status: 'idle' })
  const bookingRequestRef = useRef(0)
  const bookingSubmissionRef = useRef(0)
  const inquirySubmissionRef = useRef(0)
  const prefillDateRef = useRef<string | undefined>(undefined)
  const composerRef = useRef<HTMLTextAreaElement>(null)
  const clearChatRef = useRef<() => void>(() => {})
  const [toast, setToast] = useState<{ type: "error" | "success"; message: string } | null>(
    initialToast,
  )
  const normalizedApiBaseUrl = useMemo(
    () => (apiBaseUrl ? apiBaseUrl.replace(/\/$/, '') : ''),
    [apiBaseUrl],
  )
  const appointmentsEndpoint = useMemo(
    () => `${normalizedApiBaseUrl || ''}/api/appointments`,
    [normalizedApiBaseUrl],
  )

  const resolvedBusinessName = brandName || businessContext?.name || 'Handle Salon & Spa'
  const assistantAvatarUrl = assistantAvatar || logoUrl || DEFAULT_ASSISTANT_AVATAR
  const assistantHeadline = assistantName ?? ASSISTANT_NAME
  const assistantSubtitle =
    assistantTagline || `your ${assistantRole ?? ASSISTANT_ROLE}`
  const resolvedServicesFromContext =
    businessContext?.services && businessContext.services.length
      ? businessContext.services
      : BUSINESS_CONTEXT.services

  const config: ClientConfig = {
    clientId: clientId || userId, // Use userId as fallback for clientId
    welcomeMessage,
    primaryColor,
    brandName: resolvedBusinessName,
    logoUrl: assistantAvatarUrl,
    userId,
    calendarSettingId,
    chatbotId,
  }

  const bookingSummary = useMemo(() => {
    if (bookingState.status !== 'success') return undefined
    const { service, selection, form, payment } = bookingState
    const summaryId = `${service.id}-${selection.date}-${selection.slot}-${payment.last4}-${payment.amountCents}`
    return {
      id: summaryId,
      service,
      selection,
      form,
      payment,
    }
  }, [bookingState])

  useEffect(() => {
    setAvatarFailed(false)
  }, [logoUrl])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const mediaQuery = window.matchMedia(`(max-width: ${mobileBreakpoint}px)`)
    const handleChange = () => setIsMobileViewport(mediaQuery.matches)
    handleChange()
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
    mediaQuery.addListener(handleChange)
    return () => mediaQuery.removeListener(handleChange)
  }, [mobileBreakpoint])

  const bookingActive = useMemo(() => bookingState.status !== 'idle', [bookingState.status]);
  const inquiryActive = useMemo(() => inquiryState.status !== 'idle', [inquiryState.status]);

  useEffect(() => {
    if (isOpen && isMobileViewport) {
      // When the widget is opened in mobile, expand it automatically
      setIsExpanded(true)
    }
  }, [isOpen, isMobileViewport])

  useEffect(() => {
    if (isOpen && !bookingActive && !inquiryActive) {
      // Focus composer when chat opens
      setTimeout(() => composerRef.current?.focus(), 100)
    }
  }, [isOpen, bookingActive, inquiryActive])

  const showToast = useCallback((message: string, type: "error" | "success" = "error") => {
    setToast({ message, type })
  }, [])

  useEffect(() => {
    if (!toast) return
    const id = setTimeout(() => setToast(null), 4200)
    return () => clearTimeout(id)
  }, [toast])

  const loadServices = useCallback(
    (serviceId?: string) => {
      bookingRequestRef.current += 1
      const requestId = bookingRequestRef.current
      setBookingState({ status: 'services-loading' })
      mockFetchServices(resolvedServicesFromContext)
        .then((services) => {
          const usableServices =
            services && services.length ? services : BUSINESS_CONTEXT.services
          if (bookingRequestRef.current !== requestId) return
          if (!usableServices.length) {
            setBookingState({ status: 'services-error' })
            return
          }
          setBookingState({
            status: 'services-ready',
            services: usableServices,
            selectedServiceId: serviceId ?? usableServices[0].id,
          })
        })
        .catch(() => {
          if (bookingRequestRef.current !== requestId) return
          // Fall back to built-in demo services instead of erroring
          if (BUSINESS_CONTEXT.services?.length) {
            setBookingState({
              status: 'services-ready',
              services: BUSINESS_CONTEXT.services,
              selectedServiceId: serviceId ?? BUSINESS_CONTEXT.services[0].id,
            })
            return
          }
          setBookingState({ status: 'services-error' })
        })
    },
    [resolvedServicesFromContext],
  )

  const beginAvailabilityLookup = useCallback((service: BookingService, isoDate?: string) => {
    bookingRequestRef.current += 1
    const requestId = bookingRequestRef.current
    setBookingState({ status: 'availability-loading', service })
    mockFetchAvailability()
      .then((days) => {
        if (bookingRequestRef.current !== requestId) return
        if (!days.length) {
          setBookingState({ status: 'availability-error', service })
          return
        }
        const normalizedDays = days.map((day) => ({
          ...day,
          date: normalizeDate(day.date),
        }))
        const normalizedSelectedDate = isoDate ? normalizeDate(isoDate) : normalizedDays[0].date
        setBookingState({
          status: 'availability-ready',
          service,
          days: normalizedDays,
          selectedDate: normalizedSelectedDate,
        })
      })
      .catch(() => {
        if (bookingRequestRef.current !== requestId) return
        setBookingState({ status: 'availability-error', service })
      })
  }, [])

  const startBookingFlow = useCallback(
    (serviceId?: string, isoDate?: string) => {
      prefillDateRef.current = isoDate
      loadServices(serviceId)
    },
    [loadServices],
  )

  const closeBooking = useCallback(() => {
    bookingRequestRef.current += 1
    bookingSubmissionRef.current += 1
    setBookingState({ status: 'idle' })
  }, [])

  const selectServiceOption = useCallback((serviceId: string) => {
    setBookingState((prev) => {
      if (prev.status !== 'services-ready') return prev
      if (prev.selectedServiceId === serviceId) return prev
      if (!prev.services.some((service) => service.id === serviceId)) return prev
      return { ...prev, selectedServiceId: serviceId }
    })
  }, [])

  const handleServiceContinue = useCallback(() => {
    setBookingState((prev) => {
      if (prev.status !== 'services-ready') return prev
      const selectedService =
        prev.services.find((service) => service.id === prev.selectedServiceId) ?? prev.services[0]
      if (!selectedService) return prev
      beginAvailabilityLookup(selectedService, prefillDateRef.current)
      return prev
    })
  }, [beginAvailabilityLookup])

  const retryAvailability = useCallback(() => {
    setBookingState((prev) => {
      if (prev.status !== 'availability-error') return prev
      beginAvailabilityLookup(prev.service)
      return prev
    })
  }, [beginAvailabilityLookup])

  const selectBookingDate = useCallback((isoDate: string) => {
    setBookingState((prev) => {
      if (prev.status !== 'availability-ready') return prev
      return { ...prev, selectedDate: isoDate.slice(0, 10) }
    })
  }, [])

  const handleSlotSelection = useCallback((selection: BookingSelection) => {
    setBookingState((prev) => {
      if (prev.status !== 'availability-ready') return prev
      return {
        status: 'details',
        service: prev.service,
        days: prev.days,
        selectedDate: selection.date,
        selection,
      }
    })
  }, [])

  const handleBookingBackToSlots = useCallback(() => {
    setBookingState((prev) => {
      if (prev.status !== 'details') return prev
      return {
        status: 'availability-ready',
        service: prev.service,
        days: prev.days,
        selectedDate: prev.selection.date,
      }
    })
  }, [])

  const slotToIso = useCallback(
    (date: string, slot: string) => {
      const match = slot.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
      if (!match) return null

      let hours = parseInt(match[1], 10)
      const minutes = parseInt(match[2], 10)
      const period = match[3].toUpperCase()

      if (period === 'PM' && hours < 12) hours += 12
      if (period === 'AM' && hours === 12) hours = 0

      const [year, month, day] = date.split('-').map((part) => parseInt(part, 10))
      if ([year, month, day, hours, minutes].some(Number.isNaN)) return null

      // If we have a timezone, map the local components to UTC for that zone
      if (timezone) {
        try {
          const utcGuess = new Date(Date.UTC(year, month - 1, day, hours, minutes))
          const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
          })
          const parts = formatter.formatToParts(utcGuess)
          const find = (type: string) => parts.find((p) => p.type === type)?.value
          const zonedUtcMs = Date.UTC(
            Number(find('year')),
            Number(find('month')) - 1,
            Number(find('day')),
            Number(find('hour')),
            Number(find('minute')),
            Number(find('second')),
          )
          return new Date(zonedUtcMs).toISOString()
        } catch (error) {
          console.warn('[slotToIso] Failed to apply timezone, falling back to local time', error)
        }
      }

      const localDate = new Date(year, month - 1, day, hours, minutes)
      return localDate.toISOString()
    },
    [timezone],
  )

  const submitAppointment = useCallback(
    async (payload: {
      service: BookingService
      selection: BookingSelection
      form: BookingForm
      payment: BookingPaymentDetails
      days?: BookingDay[]
    }) => {
      const startIso = slotToIso(payload.selection.date, payload.selection.slot)
      if (!startIso) {
        console.error('[submitAppointment] Could not parse selected slot to ISO date')
        setBookingState((prev) => {
          if (prev.status === 'submitting') return prev
          return {
            status: 'details',
            service: payload.service,
            days: payload.days ?? [],
            selectedDate: payload.selection.date,
            selection: payload.selection,
          }
        })
        showToast("Could not parse the selected time. Please pick another slot.")
        return
      }

      const endIso = new Date(
        new Date(startIso).getTime() + payload.service.durationMinutes * 60 * 1000,
      ).toISOString()

      bookingSubmissionRef.current += 1
      const submissionId = bookingSubmissionRef.current

      setBookingState({
        status: 'submitting',
        ...payload,
      })

      try {
        const includePayment =
          payload.payment && payload.payment.amountCents > 0 && payload.payment.status !== 'not_required'

        const response = await fetch(appointmentsEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            businessId: businessId || undefined,
            serviceId: payload.service.id,
            customerName: payload.form.fullName,
            customerEmail: payload.form.email,
            customerPhone: undefined,
            startTime: startIso,
            endTime: endIso,
            chatbotId,
            ...(includePayment ? { payment: payload.payment } : {}),
          }),
        })

        const result = await response.json().catch(() => null)

        if (bookingSubmissionRef.current !== submissionId) return

        if (!response.ok || result?.success === false) {
          const errorMessage = result?.error || 'Unable to confirm appointment'
          console.error('[submitAppointment] API rejected booking:', errorMessage)
          showToast(
            errorMessage === "Selected slot is no longer available"
              ? "That time was just taken. Please choose another slot."
              : errorMessage,
          )
          setBookingState({
            status: 'details',
            service: payload.service,
            days: payload.days ?? [],
            selectedDate: payload.selection.date,
            selection: payload.selection,
          })
          return
        }

        setBookingState({
          status: 'success',
          ...payload,
        })
        emitEvent?.({
          type: 'booking_confirmed',
          clientId: clientId || userId,
          content: `Service: ${payload.service.name}; Slot: ${payload.selection.date} ${payload.selection.slot}`,
        })
      } catch (error) {
        console.error('[submitAppointment] Failed to submit booking:', error)
        if (bookingSubmissionRef.current !== submissionId) return
        showToast("We couldn't book that slot. Please try again.")
        setBookingState({
          status: 'details',
          service: payload.service,
          days: payload.days ?? [],
          selectedDate: payload.selection.date,
          selection: payload.selection,
        })
      }
    },
    [appointmentsEndpoint, businessId, chatbotId, clientId, emitEvent, slotToIso, userId],
  )

  const handleBookingFormSubmit = useCallback(
    (form: BookingForm) => {
      setBookingState((prev) => {
        if (prev.status !== 'details') return prev

        // Skip payment for free services (price = $0)
        if (prev.service.priceCents === 0) {
          const payload = {
            service: prev.service,
            selection: prev.selection,
            form,
            payment: {
              method: 'stripe' as const,
              brand: 'N/A',
              last4: '0000',
              amountCents: 0,
              status: 'confirmed' as const,
            },
            days: prev.days,
          }
          submitAppointment(payload)
          return { status: 'submitting', ...payload }
        }

        return {
          status: 'payment',
          service: prev.service,
          selection: prev.selection,
          form,
          days: prev.days,
        }
      })
    },
    [submitAppointment],
  )

  const handlePaymentBack = useCallback(() => {
    setBookingState((prev) => {
      if (prev.status !== 'payment') return prev
      return {
        status: 'details',
        service: prev.service,
        days: prev.days,
        selectedDate: prev.selection.date,
        selection: prev.selection,
      }
    })
  }, [])

  const handlePaymentSubmit = useCallback((payment: BookingPaymentDetails) => {
    setBookingState((prev) => {
      if (prev.status !== 'payment') return prev
      const payload = {
        service: prev.service,
        selection: prev.selection,
        form: prev.form,
        payment,
        days: prev.days,
      }
      submitAppointment(payload)
      return { status: 'submitting', ...payload }
    })
  }, [submitAppointment])

  const handleBackToAvailability = useCallback(() => {
    bookingSubmissionRef.current += 1
    setBookingState((prev) => {
      if (prev.status === 'submitting' || prev.status === 'success') {
        beginAvailabilityLookup(prev.service)
      }
      return prev
    })
  }, [beginAvailabilityLookup])

  // Inquiry handlers
  const startInquiryFlow = useCallback(() => {
    setInquiryState({ status: 'form' })
  }, [])

  const closeInquiry = useCallback(() => {
    inquirySubmissionRef.current += 1
    setInquiryState({ status: 'idle' })
  }, [])

  const handleInquiryFormSubmit = useCallback((form: InquiryFormData) => {
    inquirySubmissionRef.current += 1
    const submissionId = inquirySubmissionRef.current
    setInquiryState({ status: 'submitting', form })

    const endpoint = `${normalizedApiBaseUrl || ''}/api/inquiry`

    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        businessId,
        publicId,
        chatbotId,
        name: form.fullName,
        email: form.email,
        question: form.question,
      }),
    })
      .then((res) => res.ok)
      .then((ok) => {
        if (inquirySubmissionRef.current !== submissionId) return
        if (ok) {
          setInquiryState({ status: 'success', form })
        } else {
          setInquiryState({ status: 'form' })
          showToast('Unable to send your message. Please try again.')
        }
      })
      .catch((error) => {
        console.error('[Inquiry] submit failed', error)
        if (inquirySubmissionRef.current !== submissionId) return
        setInquiryState({ status: 'form' })
        showToast('Unable to send your message. Please try again.')
      })
  }, [businessId, chatbotId, publicId, normalizedApiBaseUrl, showToast])

  const updatePanelState = useCallback(
    (resolver: (previous: boolean) => boolean) => {
      setIsOpen((prev) => {
        const next = resolver(prev)
        if (next === prev) return prev
        if (!next) setIsExpanded(false)
        emitEvent?.({ type: next ? 'chat_opened' : 'chat_closed', clientId: clientId || userId })
        return next
      })
    },
    [clientId, userId, emitEvent],
  )

  const togglePanel = useCallback(() => {
    updatePanelState((prev) => !prev)
  }, [updatePanelState])

  const closePanel = useCallback(() => {
    setIsExpanded(false)
    updatePanelState(() => false)
  }, [updatePanelState])

  const handleToggleExpand = useCallback(() => {
    if (isMobileViewport) {
      // On mobile, "expanding" is the default state, so this button should close the panel
      closePanel()
      return
    }
    setIsExpanded((prev) => !prev)
  }, [isMobileViewport, closePanel])

  const showServiceSelection = ['services-loading', 'services-error', 'services-ready'].includes(bookingState.status)
  const showAvailability = ['availability-loading', 'availability-error', 'availability-ready'].includes(
    bookingState.status,
  )

  const themeStyle = {
    '--byh-primary': primaryColor,
    '--byh-bg-color': colors.backgroundColor,
    '--byh-text-color': colors.textColor,
    '--byh-primary-color': colors.primaryColor,
    '--byh-accent-color': colors.accentColor,
    '--byh-border-color': colors.borderColor,
    '--byh-button-color': colors.buttonColor,
    '--byh-button-hover-color': colors.buttonHoverColor,
    '--byh-error-color': colors.errorColor,
    '--byh-success-color': colors.successColor,
    '--byh-warning-color': colors.warningColor,
    '--byh-launcher-bg-color': colors.launcherBackgroundColor,
    '--byh-header-bg-color': colors.headerBackgroundColor,
    '--byh-composer-bg-color': colors.composerBackgroundColor,
    '--byh-panel-bg-color': colors.panelBackgroundColor,
    '--byh-font-family': typography.fontFamily,
    '--byh-font-size': typography.fontSize,
    '--byh-font-weight': typography.fontWeight,
    '--byh-heading-font-family': typography.headingFontFamily,
    '--byh-heading-font-weight': typography.headingFontWeight,
  } as CSSProperties

  const positionMap: Record<
    NonNullable<WidgetAppProps['position']>,
    { container: string; origin: string }
  > = {
    'bottom-right': { container: 'bottom-5 right-5 items-end', origin: 'origin-bottom-right' },
    'bottom-left': { container: 'bottom-5 left-5 items-start', origin: 'origin-bottom-left' },
    'top-right': { container: 'top-5 right-5 items-end', origin: 'origin-top-right' },
    'top-left': { container: 'top-5 left-5 items-start', origin: 'origin-top-left' },
  }

  const positionClasses = positionMap[position] ?? positionMap['bottom-right']

  const isFullScreen = isExpanded && isMobileViewport

  const containerStyle: CSSProperties = {
    ...themeStyle,
    width: isFullScreen ? '100%' : isExpanded ? expandedWidth : `${panelWidth}px`,
    maxWidth: isFullScreen ? '100vw' : 'calc(100vw - 32px)',
    height: isFullScreen ? '100%' : undefined,
    zIndex: zIndex,
  }

  const panelStyle: CSSProperties = {
    height: isFullScreen ? '100%' : isExpanded ? expandedHeight : `${panelHeight}px`,
    maxHeight: isExpanded && !isMobileViewport ? 'calc(100vh - 40px)' : undefined,
  }

  const panelContentClasses = [
    'flex min-h-0 flex-col gap-2 bg-slate-50/40 flex-1 overflow-y-auto',
    isMobileViewport ? 'px-3 pb-0 pt-3' : 'px-2 pb-0 pt-3',
  ].join(' ')

  const containerClasses = [
    'pointer-events-none fixed flex flex-col transition-all',
    isMobileViewport ? 'gap-0' : 'gap-2',
    isFullScreen ? 'inset-0 items-stretch' : positionClasses.container,
  ].join(' ')

  const panelClasses = [
    'w-full flex flex-col border border-slate-200/40 bg-white shadow-sm transition-transform',
    positionClasses.origin,
    isFullScreen ? 'rounded-none' : 'rounded-lg',
    isOpen
      ? 'pointer-events-auto translate-y-0 scale-100 opacity-100'
      : 'pointer-events-none translate-y-3 scale-95 opacity-0',
  ].join(' ')

  return (
    <div className={containerClasses} style={containerStyle}>
      {toast && (
        <div className="pointer-events-none fixed inset-x-4 top-4 z-[2147483601] sm:left-auto sm:right-6 sm:inset-x-auto">
          <div
            className={`pointer-events-auto flex items-start gap-3 rounded-lg border px-4 py-3 shadow-lg ${
              toast.type === "error"
                ? "border-red-200 bg-white text-red-800"
                : "border-green-200 bg-white text-green-800"
            }`}
            role="status"
          >
            <div className="mt-0.5">
              {toast.type === "error" ? (
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 9v4m0 4h.01" />
                  <circle cx="12" cy="12" r="9" />
                </svg>
              ) : (
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 13l4 4L19 7" />
                  <circle cx="12" cy="12" r="9" />
                </svg>
              )}
            </div>
            <p className="text-sm font-medium">{toast.message}</p>
            <button
              type="button"
              onClick={() => setToast(null)}
              className="ml-auto text-xs font-semibold text-gray-500 hover:text-gray-700"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
      <section className={panelClasses} style={panelStyle}>
        <WidgetHeader
          brandName={assistantHeadline}
          availabilityText={assistantSubtitle}
          logoUrl={assistantAvatarUrl}
          logoFailed={avatarFailed}
          onLogoError={() => setAvatarFailed(true)}
          isExpanded={isExpanded}
          onToggleExpand={handleToggleExpand}
          onClose={closePanel}
          onClearChat={() => clearChatRef.current?.()}
        />

        <MessageProvider
          apiBaseUrl={apiBaseUrl}
          clientId={clientId || userId}
          userId={userId}
          chatbotId={chatbotId}
          publicId={publicId}
          welcomeMessage={welcomeMessage}
          emitEvent={emitEvent}
          onAutoStartBooking={(serviceId, isoDate) => startBookingFlow(serviceId, isoDate)}
          onAutoStartInquiry={startInquiryFlow}
          bookingSummary={bookingSummary}
          onRegisterClear={(fn) => {
            clearChatRef.current = fn
          }}
        >
          <div className={panelContentClasses}>
              <SuggestionCards
                bookingActive={bookingActive}
                inquiryActive={inquiryActive}
                onStartBooking={() => startBookingFlow()}
                onStartInquiry={startInquiryFlow}
                ctaLabels={ctaLabels}
              />
              {showServiceSelection && (
                <BookingServiceSelection
                  state={bookingState as Extract<
                    BookingState,
                    { status: 'services-loading' | 'services-error' | 'services-ready' }
                  >}
                  onClose={closeBooking}
                  onRetry={() => startBookingFlow()}
                  onSelectService={selectServiceOption}
                  onContinue={handleServiceContinue}
                />
              )}
              {showAvailability && (
                <BookingAvailability
                  state={bookingState as Extract<
                    BookingState,
                    { status: 'availability-loading' | 'availability-error' | 'availability-ready' }
                  >}
                  onClose={closeBooking}
                  onRetry={retryAvailability}
                  onChangeService={() => startBookingFlow()}
                  onSelectDate={selectBookingDate}
                  onSelectSlot={handleSlotSelection}
                  header={headers.bookAppointment}
                />
              )}
              {bookingState.status === 'details' && (
                <BookingDetails state={bookingState} onBack={handleBookingBackToSlots} onClose={closeBooking} onSubmit={handleBookingFormSubmit} />
              )}
              {bookingState.status === 'payment' && (
                <BookingPayment
                  service={bookingState.service}
                  selection={bookingState.selection}
                  form={bookingState.form}
                  onBack={handlePaymentBack}
                  onClose={closeBooking}
                  onConfirmPayment={handlePaymentSubmit}
                />
              )}
              {bookingState.status === 'submitting' && (
                <BookingSubmitting
                  service={bookingState.service}
                  selection={bookingState.selection}
                  payment={bookingState.payment}
                  onBack={handleBackToAvailability}
                />
              )}
              {bookingState.status === 'success' && (
                <BookingSuccess
                  service={bookingState.service}
                  selection={bookingState.selection}
                  form={bookingState.form}
                  payment={bookingState.payment}
                  onClose={closeBooking}
                  onBack={handleBackToAvailability}
                  successMessages={successMessages}
                />
              )}
              {inquiryState.status === 'form' && (
                <InquiryForm
                  config={config}
                  onClose={closeInquiry}
                  onSubmit={handleInquiryFormSubmit}
                  header={headers.leaveMessage}
                />
              )}
              {inquiryState.status === 'submitting' && (
                <InquirySubmitting form={inquiryState.form} />
              )}
              {inquiryState.status === 'success' && (
                <InquirySuccess
                  form={inquiryState.form}
                  onClose={closeInquiry}
                />
              )}
              {!bookingActive && !inquiryActive && (
                <ChatTranscript
                  onStartBooking={(serviceId) => startBookingFlow(serviceId)}
                  onStartInquiry={startInquiryFlow}
                  phoneNumber={phoneNumber}
                />
              )}
            </div>
            {!bookingActive && !inquiryActive && (
              <div className="border-t border-slate-200/60 bg-white px-4 py-3">
                <Composer
                  ref={composerRef}
                  isMobileViewport={isMobileViewport}
                  placeholder={composerPlaceholder}
                  placeholderLoading={composerPlaceholderLoading}
                />
              </div>
            )}
          </MessageProvider>
      </section>

      <ChatLauncher
        isOpen={isOpen}
        logoUrl={assistantAvatarUrl}
        tooltipMessage={launcherMessage || ''}
        tooltipDelay={tooltipDelay}
        assistantName={assistantHeadline}
        onToggle={togglePanel}
      />
    </div>
  )
}
